import React, { useEffect, useCallback, useState } from 'react';
import { RgbaColorPicker } from 'react-colorful';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useColorPicker, RGBAColor } from '../hooks/useColorPicker';
import { useSticker } from '../context/StickerContext';
import { Pipette, Palette, Sliders, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { useEyeDropper } from '../hooks/useEyeDropper';
import { useWindow } from '../hooks/useWindow';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
}

const ColorPickerTabs = {
  picker: 'picker',
  rgb: 'RGB',
  cmyk: 'CMYK'
} as const;

export default function ColorPicker({ color, onChange }: ColorPickerProps) {
  const { canvas } = useSticker();
  const { isMobile } = useWindow();
  const {
    color: rgbaColor,
    cmykValues,
    handleColorChange,
    handleCMYKChange,
    getRgbaString
  } = useColorPicker(color);
  
  // Track input focus for visual feedback
  const [focusedInput, setFocusedInput] = useState<string | null>(null);

  const { isActive, startEyeDropper, stopEyeDropper, handleEyeDropper } = useEyeDropper(canvas);

  const handleRGBAChange = useCallback((newColor: RGBAColor) => {
    const hexColor = handleColorChange(newColor);
    if (typeof onChange === 'function') {
      onChange(hexColor);
    } else {
      console.error('ColorPicker: onChange is not a function', onChange);
    }
  }, [handleColorChange, onChange]);

  const handleCMYKUpdate = useCallback((key: keyof typeof cmykValues, value: number) => {
    const hexColor = handleCMYKChange(key, value);
    if (typeof onChange === 'function') {
      onChange(hexColor);
    } else {
      console.error('ColorPicker: onChange is not a function', onChange);
    }
  }, [handleCMYKChange, onChange]);

  // Set up eyedropper event handlers
  useEffect(() => {
    if (isActive) {
      return handleEyeDropper((pickedColor) => {
        if (typeof onChange === 'function') {
          onChange(pickedColor);
        } else {
          console.error('ColorPicker: onChange is not a function', onChange);
        }
      });
    }
  }, [isActive, handleEyeDropper, onChange]);

  const renderRGBInputs = useCallback(() => (
    <div className="grid grid-cols-2 gap-4">
      {(['r', 'g', 'b'] as const).map((key) => (
        <div key={key} className="space-y-1.5">
          <Label className="flex items-center text-sm font-medium">
            <div className={`w-5 h-5 rounded-sm mr-2 ${key === 'r' ? 'bg-red-500/80' : key === 'g' ? 'bg-green-500/80' : 'bg-blue-500/80'}`}></div>
            {key === 'r' ? 'Red' : key === 'g' ? 'Green' : 'Blue'}
          </Label>
          <div className="relative">
            <Input
              type="number"
              min={0}
              max={255}
              value={rgbaColor[key]}
              className={`pr-10 ${focusedInput === key ? 'ring-2 ring-primary/50' : ''}`}
              onFocus={() => setFocusedInput(key)}
              onBlur={() => setFocusedInput(null)}
              onChange={(e) => {
                const newColor = { ...rgbaColor, [key]: Number(e.target.value) };
                handleRGBAChange(newColor);
              }}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              /255
            </div>
          </div>
        </div>
      ))}
      <div className="space-y-1.5">
        <Label className="flex items-center text-sm font-medium">
          <div className="w-5 h-5 rounded-sm mr-2 bg-gray-400/50"></div>
          Alpha
        </Label>
        <div className="relative">
          <Input
            type="number"
            min={0}
            max={1}
            step="0.01"
            value={rgbaColor.a}
            className={`pr-8 ${focusedInput === 'a' ? 'ring-2 ring-primary/50' : ''}`}
            onFocus={() => setFocusedInput('a')}
            onBlur={() => setFocusedInput(null)}
            onChange={(e) => {
              const newColor = { ...rgbaColor, a: Number(e.target.value) };
              handleRGBAChange(newColor);
            }}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            /1
          </div>
        </div>
      </div>
    </div>
  ), [rgbaColor, handleRGBAChange, focusedInput]);

  const renderCMYKInputs = useCallback(() => (
    <div className="grid grid-cols-2 gap-4">
      {(Object.keys(cmykValues) as Array<keyof typeof cmykValues>).map((key) => {
        const label = key === 'c' ? 'Cyan' : key === 'm' ? 'Magenta' : key === 'y' ? 'Yellow' : 'Key (Black)';
        const color = key === 'c' ? 'bg-cyan-500/80' : key === 'm' ? 'bg-pink-500/80' : key === 'y' ? 'bg-yellow-500/80' : 'bg-gray-800/80';
        
        return (
          <div key={key} className="space-y-1.5">
            <Label className="flex items-center text-sm font-medium">
              <div className={`w-5 h-5 rounded-sm mr-2 ${color}`}></div>
              {label}
            </Label>
            <div className="relative">
              <Input
                type="number"
                min={0}
                max={100}
                value={cmykValues[key]}
                className={`pr-8 ${focusedInput === key ? 'ring-2 ring-primary/50' : ''}`}
                onFocus={() => setFocusedInput(key)}
                onBlur={() => setFocusedInput(null)}
                onChange={(e) => handleCMYKUpdate(key, Number(e.target.value))}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                %
              </div>
            </div>
          </div>
        );
      })}
    </div>
  ), [cmykValues, handleCMYKUpdate, focusedInput]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between mb-4">
        <Label className="text-base font-semibold">Background Color</Label>
        {!isMobile && (
          <Button
            variant="outline"
            size="sm"
            className={`ml-4 ${isActive ? 'bg-primary/10 border-primary text-primary' : ''}`}
            onClick={() => isActive ? stopEyeDropper() : startEyeDropper()}
          >
            <Pipette className="w-3.5 h-3.5 mr-1.5" />
            {isActive ? 'Cancel' : 'Pick Color'}
          </Button>
        )}
      </div>
      
      <Tabs defaultValue={ColorPickerTabs.picker} className="w-full">
        <TabsList className="w-full grid grid-cols-3 p-1 bg-muted/50 rounded-lg">
          <TabsTrigger 
            value={ColorPickerTabs.picker}
            className="flex items-center justify-center data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md"
          >
            <Palette className="w-3.5 h-3.5 mr-1.5" />
            Picker
          </TabsTrigger>
          <TabsTrigger 
            value={ColorPickerTabs.rgb}
            className="flex items-center justify-center data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md"
          >
            <Sliders className="w-3.5 h-3.5 mr-1.5" />
            RGB
          </TabsTrigger>
          <TabsTrigger 
            value={ColorPickerTabs.cmyk}
            className="flex items-center justify-center data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md"
          >
            <Layers className="w-3.5 h-3.5 mr-1.5" />
            CMYK
          </TabsTrigger>
        </TabsList>

        <TabsContent value={ColorPickerTabs.picker} className="flex justify-center pt-4">
          <RgbaColorPicker color={rgbaColor} onChange={handleRGBAChange} />
        </TabsContent>

        <TabsContent value={ColorPickerTabs.rgb} className="space-y-4 pt-4">
          {renderRGBInputs()}
        </TabsContent>

        <TabsContent value={ColorPickerTabs.cmyk} className="space-y-4 pt-4">
          {renderCMYKInputs()}
        </TabsContent>
      </Tabs>

      <div 
        className="w-full h-12 rounded-md border shadow-inner"
        style={{ backgroundColor: getRgbaString() }}
      />
    </div>
  );
}